import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/site/ProductCard";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { Faq, faqLd } from "@/components/site/Faq";
import { inline } from "@/components/site/RichText";
import { FormationBox } from "@/components/site/FormationBox";
import { FormationGuideCta } from "@/components/formation/FormationGuideCta";
import { FlashHero } from "@/components/site/FlashSale";
import { useSeo, SITE_URL, ORGANIZATION, FOUNDER } from "@/lib/seo";
import { EN_PAGES } from "@/lib/enPages";
import { EnGuidesNav } from "@/pages/EnBlog";
import { ShopReviewsHighlight } from "@/components/site/ShopReviews";

const NAME = Object.fromEntries(Object.values(EN_PAGES).map((p) => [p.path, p.name]));

function EnProducts({ slugs }) {
  const { data } = useProducts();
  const all = Array.isArray(data) ? data : [];
  const list = slugs.map((s) => all.find((p) => p.slug === s)).filter(Boolean);
  if (!list.length) return null;
  return (
    <section data-testid="en-products" className="mt-24">
      <h2 className="mb-3 font-display text-3xl sm:text-4xl">Ozentya products for this routine</h2>
      <p className="mb-10 text-sm text-[var(--parchment)]/80">Prices in euros with an approximate amount in your currency; shipping to your country is shown in the cart.</p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">{list.map((p, i) => <ProductCard key={p.slug} p={p} index={i} details />)}</div>
    </section>
  );
}

export default function EnPage({ id }) {
  const t = EN_PAGES[id];
  const crumbs = [{ to: "/en", label: "Ozentya (English)" }, ...(t.path === "/en" ? [] : [{ to: t.path, label: t.name }])];
  useSeo({
    title: t.title, description: t.description, path: t.path, lang: "en", alternates: t.alt,
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "WebPage", name: t.h1, url: `${SITE_URL}${t.path}`, description: t.description, inLanguage: "en", publisher: ORGANIZATION },
      breadcrumbLd(SITE_URL, crumbs), ...(t.faq?.length ? [faqLd(t.faq)] : []),
      ...(t.updated ? [{ "@type": "Article", headline: t.h1, description: t.description, url: `${SITE_URL}${t.path}`, mainEntityOfPage: `${SITE_URL}${t.path}`, inLanguage: "en",
        author: FOUNDER, publisher: { "@id": ORGANIZATION["@id"] }, datePublished: t.updated, dateModified: t.updated }] : []),
    ] },
  });
  return (
    <main lang="en" data-testid={`en-page-${id}`} className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={crumbs} />
      {t.path === "/en" ? <FlashHero className="mb-12" /> : null}
      <p className="eyebrow mb-6">{t.eyebrow}</p>
      <h1 data-testid="en-title" className="max-w-5xl font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">{t.h1}</h1>
      <p className="mt-8 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{t.intro}</p>
      {t.updated ? <p data-testid="en-byline" className="mt-6 text-sm text-[var(--parchment)]/85">By <Link to="/a-propos" className="text-[var(--gold-soft)] underline underline-offset-4">Pascal Chaubo</Link>, founder of Ozentya (afro hair care since 2011) · updated <time dateTime={t.updated}>{new Date(`${t.updated}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</time></p> : null}
      {t.answer ? (
        <div data-testid="en-answer" className="mt-8 max-w-3xl border-l-2 border-[var(--gold)] bg-[var(--ink-2)] p-5 md:p-6">
          <p className="eyebrow mb-2">In short</p>
          <p className="text-sm leading-relaxed text-[var(--parchment)] md:text-base">{t.answer}</p>
        </div>
      ) : null}
      {t.notice ? (
        <p className="mt-8 flex max-w-3xl gap-3 rounded-sm border border-[var(--gold)]/40 bg-[rgba(217,180,106,0.08)] p-5 text-sm leading-relaxed text-[var(--parchment)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" aria-hidden="true" />{t.notice}
        </p>
      ) : null}
      {t.group === "formation" ? <FormationGuideCta en testid="en-formation-cta-top" className="mt-8 max-w-3xl" /> : null}
      <div className="mt-16 grid gap-14 border-t border-[var(--line)] pt-16 lg:grid-cols-2">
        {t.sections.map((s) => (
          <section key={s.h2} className="max-w-2xl">
            <h2 className="font-display text-3xl leading-tight">{s.h2}</h2>
            <p className="mt-4 text-sm leading-relaxed text-[var(--parchment)]/85 md:text-base">{inline(s.p, s.h2)}</p>
          </section>
        ))}
      </div>
      <EnProducts slugs={t.products} />
      {t.products.length ? <div className="-mx-5 md:-mx-10"><ShopReviewsHighlight /></div> : null}
      <Faq items={t.faq} title="Frequently asked questions" />
      {t.group === "formation" ? <FormationGuideCta en testid="en-formation-cta-bottom" className="mx-auto mt-16 max-w-4xl" /> : <FormationBox src="guide" testid="en-formation-box" className="mx-auto mt-16 max-w-4xl" />}
      <section data-testid="en-related" className="mt-20">
        <h2 className="eyebrow mb-5">Related pages</h2>
        <div className="flex flex-wrap gap-2">
          {[...t.related, "/en/blog", t.fr].map((to) => (
            <Link key={to} to={to} data-testid={`en-related-${to.replace(/\//g, "-")}`} className="flex h-10 items-center rounded-full border border-[var(--line)] px-5 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">
              {to === t.fr ? "Version française" : to === "/en/blog" ? "Hair care blog" : NAME[to]}
            </Link>
          ))}
        </div>
        {t.path === "/en" ? <><h2 className="eyebrow mb-0 mt-12">All English guides</h2><EnGuidesNav /></> : null}
      </section>
    </main>
  );
}
